import { PropsWithChildren, useMemo } from "react";

import { Box, ItemElementProps, Menu } from "@nestoca/ui";
import {
  useGetApplicantById,
  useGetApplicants,
} from "@shared/api/hooks/applications";
import { DatadogMask } from "@shared/datadog";
import { getApplicantName } from "@shared/utils";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";

import { MenuTriggerButton } from "../action-bar/menu-trigger-button";

interface ApplicantSelectorProps {
  applicationId: number;
  selectedApplicantId?: number;
  getOptionHref?: (applicantId: number) => string;
  /** default value = false */
  withGarantor?: boolean;
}

const getOptionHrefDefault = (applicationId: number) => (applicantId: number) =>
  `/applications/${applicationId}/applicant/${applicantId}`;

export const ApplicantSelectorMenu = ({
  applicationId,
  selectedApplicantId = 0,
  getOptionHref,
  withGarantor = false,
}: ApplicantSelectorProps) => {
  const { t } = useTranslation("applications");
  const { data: applicants } = useGetApplicants(applicationId, withGarantor);
  const { data: selectedApplicant } = useGetApplicantById(
    applicationId,
    selectedApplicantId
  );
  const { replace } = useRouter();

  const menuItems = useMemo(
    () =>
      applicants?.map<ItemElementProps>((applicant) => {
        const applicantName = getApplicantName(
          applicant,
          t("unknownName", { ns: "common" })
        );

        return {
          id: `${applicant.applicantId}`,
          key: `${applicant.applicantId}`,
          title: <ApplicantName>{applicantName}</ApplicantName>,
          isSelected: applicant.applicantId === selectedApplicantId,
          onClick: () => {
            const href =
              getOptionHref?.(applicant.applicantId) ||
              getOptionHrefDefault(applicationId)(applicant.applicantId);

            replace(href);
          },
        };
      }) || [],
    [applicants, selectedApplicantId, t, getOptionHref, applicationId, replace]
  );

  if (!applicants) return null;

  const Button = (isMenuOpen?: boolean) => {
    const applicantName = getApplicantName(
      selectedApplicant,
      t("unknownName", { ns: "common" })
    );

    return (
      <MenuTriggerButton
        isMenuOpen={isMenuOpen}
        ddActionName="downdown applicant"
      >
        {applicantName}
      </MenuTriggerButton>
    );
  };

  return (
    <>
      {applicants?.length > 1 ? (
        <Menu
          menuItems={[{ sectionAtoms: menuItems }]}
          buttonChildren={Button}
          e2ePrefix="applicant-selector"
          selectionMode="single"
          allowBackgroundScroll
        />
      ) : (
        <DatadogMask
          as={Box}
          data-dd-action-name="menu option applicant"
          data-testid="applicant-name"
        >
          {selectedApplicant?.firstName} {selectedApplicant?.lastName}
        </DatadogMask>
      )}
    </>
  );
};

const ApplicantName = ({ children }: PropsWithChildren) => {
  return (
    <DatadogMask as={Box} data-dd-action-name="menu option applicant">
      {children}
    </DatadogMask>
  );
};
